import { useCallback } from 'react';
import { css } from '@emotion/react';
import { Mute as Muted } from '@core/assets/Mute';
import { UnMute as UnMuted } from '@core/assets/UnMute';
import { PRODUCT_CARD_CAROUSEL_MUTE_BUTTON } from '@core/components/testIds';
import { isFalsy, isTruthy } from '@core/helpers/boolean';
import { hasCTA } from '@core/helpers/video/hasCTA';
import { hasProducts } from '@core/helpers/video/hasProducts';
import { useFeatureFlags } from '@core/hooks/useFeatureFlags';
import { useMeasure } from '@core/hooks/useMeasure';
import { useContextTranslation } from '@core/i18n';
import { isLiveStream } from '@core/livestream/helpers';
import { ProductTagPlacement } from '@core/types';
import { isMobileWeb } from '@core/userAgent/utils';
import { useStore } from '@core/widgets/store';
import { LiveStreamingDateTime } from '@embed/components/common/LiveStreaming/LiveStreamingDateTime';
import { SponsoredText } from '@embed/components/common/SponsoredText';
import UnstyledButton from '@embed/components/common/UnstyledButton';
import { ProductTag } from '@embed/components/Grid/ProductTag';
import PlayButton from '@embed/components/Grid/ThumbnailBody/PlayButton';
import { Badge, Caption, HeaderWrapper, LockWrapper, Meta, MuteButtonWrapper, Wrapper, } from '@embed/components/Grid/ThumbnailBody/ThumbnailBody.styles';
import { detectWordpress, removeEmojis } from '@embed/helpers';
import { GateLockIcon } from '@player/features/gatedVideo';
import { EMBED_THUMBNAIL_LIVESTREAM_DATE_WRAPPER, EMBED_THUMBNAIL_META_DATA_WRAPPER, } from '@player/testIds';
import { CTAButton } from '../CTAButton';
import { CTAButtonActionsWrapper } from '../CTAButton/CTAButton.styles';
import { ThumbnailProductCardCarousel } from './ThumbnailProductCardCarousel';
import useCaptionTimeout from './useCaptionTimeout';
const IS_WORDPRESS = detectWordpress();
/**
 *
 */
export const ThumbnailBody = (props) => {
    const { children, isInViewport, isLarge, isVideoLocked, isVideoPlaying, params, video, showV2Card, isMouseOver, } = props;
    let { caption, locale } = video;
    const livestreamVideo = useStore((state) => state.videos[video.id]);
    const { toggleMute } = useStore((state) => state.actions);
    const muted = useStore((state) => state.muted);
    const { t } = useContextTranslation();
    const { ref: wrapperRef, rect: { width, height }, } = useMeasure();
    // Padding for inner elements calculated as
    // 7% of the smallest dimension of the container (box).
    const padding = 0.07 * Math.min(width || 0, height || 0);
    if (IS_WORDPRESS && caption) {
        caption = removeEmojis(caption);
    }
    const videoHasProducts = hasProducts(video);
    // params is passed as a component arg, so don't use default from store
    const featureFlags = useFeatureFlags({
        channel: params.channel,
        channel_id: params.channel_id,
        target: params.target,
    });
    const productTag = !isFalsy(params.product_tag_placement) && videoHasProducts ? (<ProductTag small={!isLarge}/>) : null;
    const displayShoppingFeatures = !params.captions || isVideoPlaying || isMouseOver;
    const hideCaption = displayShoppingFeatures || !!showV2Card || params.size === 'small';
    const [isCaptionHidden] = useCaptionTimeout(hideCaption, {
        delayCaption: displayShoppingFeatures ? 0 : 2000,
    });
    const isPlayerDeckLayout = params?.player_deck_layout;
    const isStoriesLayout = params?.mode === 'stories';
    const onMuteButtonClickHandler = useCallback(() => {
        toggleMute();
    }, [toggleMute]);
    const muteLabel = muted
        ? t('Unmute video {{videoCaption}}', {
            videoCaption: video?.caption || '',
        })
        : t('Mute video {{videoCaption}}', {
            videoCaption: video?.caption || '',
        });
    return (<Wrapper ref={wrapperRef}>
      <Badge css={css `
          z-index: 2;
        `}>
        <SponsoredText hide={params?.ads_hide_badge} video={video}/>
      </Badge>

      <PlayButton aria-label={t(`Play Video`)} css={css `
          z-index: 2;
        `} className="focusable-play-button" hidePlayIcon={isVideoPlaying || isMobileWeb()} tabIndex={isInViewport ? undefined : -1}/>

      {(params.product_tag_placement === ProductTagPlacement.TOP &&
            Boolean(productTag)) ||
            isVideoLocked ? (<HeaderWrapper padding={padding} dir={params.dir}>
          {isVideoLocked && !isStoriesLayout ? (<LockWrapper dir={params.dir}>
              <GateLockIcon width={32} height={32}/>
            </LockWrapper>) : (<div />)}
          {productTag}
        </HeaderWrapper>) : null}

      {Boolean(params.player_deck_layout) && (<MuteButtonWrapper>
          <UnstyledButton onClick={onMuteButtonClickHandler} aria-label={muteLabel} data-testid={PRODUCT_CARD_CAROUSEL_MUTE_BUTTON}>
            {muted ? (<Muted aria-hidden label=""/>) : (<UnMuted aria-hidden label=""/>)}
          </UnstyledButton>
        </MuteButtonWrapper>)}

      {isLiveStream(video) && !isStoriesLayout ? (<span aria-hidden data-testid={EMBED_THUMBNAIL_LIVESTREAM_DATE_WRAPPER}>
          <LiveStreamingDateTime css={css `
              position: absolute;
              z-index: 2;
            `} video={livestreamVideo ?? video}/>
        </span>) : null}

      {(isTruthy(params.thumbnail_cta) ||
            Boolean(featureFlags.player_cta_in_carousel)) &&
            hasCTA(video) &&
            !isPlayerDeckLayout &&
            !isStoriesLayout ? (<CTAButtonActionsWrapper isHidden={!displayShoppingFeatures || !!showV2Card}>
          <CTAButton video={video}/>
        </CTAButtonActionsWrapper>) : null}

      {(isTruthy(params.thumbnail_product_card) ||
            Boolean(featureFlags.player_product_cards_in_carousel)) &&
            videoHasProducts &&
            !isPlayerDeckLayout &&
            !isStoriesLayout ? (<ThumbnailProductCardCarousel availableWidth={width} availableHeight={height} padding={padding} video={video} isHidden={!displayShoppingFeatures && !isMouseOver}/>) : null}

      {isStoriesLayout ? null : (<Meta css={css `
            z-index: 2;
          `} className="fw-meta" isCaptionHidden={isCaptionHidden || !caption} containerWidth={width} containerHeight={height} padding={padding} data-testid={EMBED_THUMBNAIL_META_DATA_WRAPPER}>
          <div style={{ flex: 1, wordBreak: 'break-word' }}>
            {caption && (<Caption aria-hidden={true} isCaptionHidden={isCaptionHidden} isLarge={isLarge} className="fw-thumbnail-caption" lang={locale || 'en'}>
                {caption}
              </Caption>)}
          </div>

          {params.product_tag_placement === ProductTagPlacement.BOTTOM &&
                Boolean(productTag) ? (<div style={{ flex: 0 }}>{productTag}</div>) : null}
        </Meta>)}

      {children}
    </Wrapper>);
};
